import React, { useEffect, useRef } from 'react';

interface AutoGrowTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Minimum visible height, in rows. The box never shrinks below this. */
  minRows?: number;
}

/**
 * A textarea that grows to fit its content instead of scrolling — so a pasted
 * request/response is shown in full. Resizes on input and whenever `value` changes
 * programmatically (e.g. "Load from execution").
 */
const AutoGrowTextarea: React.FC<AutoGrowTextareaProps> = ({
  value,
  minRows = 4,
  className,
  style,
  onInput,
  ...rest
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    resize();
  }, [value]);

  return (
    <textarea
      ref={ref}
      value={value}
      className={className}
      style={{ resize: 'none', overflow: 'hidden', minHeight: `${minRows * 1.5}em`, ...style }}
      onInput={(e) => {
        resize();
        onInput?.(e);
      }}
      {...rest}
    />
  );
};

export default AutoGrowTextarea;
